import React, {useCallback, useEffect, useState} from "react";
import {Box, IconButton, Paper, TextField, Tooltip, Typography} from "@mui/material";
import {CheckCircleOutlined, DeleteForever, DoNotDisturbAltOutlined} from "@mui/icons-material";
import CCButton from "../Common/CCButton";
import css_buttons from "../Common/css/CCButton.module.scss";
import AxiosFoundationAPI from "../../util/AxiosFoundationAPI";
import {V1ChangeOrderTaskIDOCToArray} from "../../util/Payload";
import {hasPrivilege} from "../../util/SessionContext";
import {CHANGE_ORDER} from "../../util/changeOrderPrivileges";
import {formatLocal} from "../../util/Formatters";
import {Blue3, Grey5, Grey6, Grey17} from "../../theme/styles";
import {approvalMessage} from "../../util/approvalMessages";

const URL = "t1/v1/change_order_tasks";
const IDOC = "v1.ChangeOrderTask-001";

/**
 * The work a change order requires before it can close.
 *
 * WHY THIS PANEL EXISTS AT ALL. Approving a change says the change is sound; it does not say the
 * work it implies has been done. A change order with unfinished tasks sits in IMPLEMENTATION --
 * signed, not ready -- and this is the only place that state can be understood or cleared.
 *
 * ADDING AND REMOVING ARE DRAFT-ONLY, because a task added after submission is work nobody reviewed.
 * COMPLETING IS NOT: it stays available long after the signatures are in, which is the whole point.
 *
 * ANYBODY WHO CAN SEE A TASK MAY TICK IT OFF, and that is deliberate rather than an omission. A task
 * is assigned to a person; requiring a privilege to report that your own work is finished would put
 * the assignee at the mercy of an administrator. The server takes the same position -- Complete
 * Change Order Task consults no privilege -- so gating it here would hide a control the server
 * allows, which is decoration rather than security.
 */
function ChangeOrderTasks({changeOrderUSID, state, onChanged}: {
    changeOrderUSID?: string;
    state?: string;
    onChanged?: () => void;
}) {
    const [tasks, setTasks] = useState<any[]>([]);
    const [instruction, setInstruction] = useState<string>("");
    const [busy, setBusy] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(() => {
        if (!changeOrderUSID) return;
        AxiosFoundationAPI({
            method: "GET", url: URL,
            params: {ChangeOrderUSID: changeOrderUSID},
            headers: {"idoc-response": IDOC},
        })
            .then((response: any) => setTasks(V1ChangeOrderTaskIDOCToArray(response.data).Rows ?? []))
            .catch(() => setTasks([]));
    }, [changeOrderUSID]);

    useEffect(() => { load(); }, [load]);

    const after = useCallback(() => {
        load();
        // THE CHANGE ORDER'S OWN STATE CAN MOVE HERE. Finishing the last task takes it out of
        // IMPLEMENTATION, so the page around this panel is stale the moment it happens.
        onChanged?.();
    }, [load, onChanged]);

    const fail = (failure: any) => setError(approvalMessage(failure, "That did not work."));

    const add = useCallback(() => {
        const text = instruction.trim();
        if (!text || !changeOrderUSID) return;
        setBusy(true);
        setError(null);
        AxiosFoundationAPI({
            method: "POST", url: URL,
            data: {PlatformMessage: {"-idoc": IDOC, ChangeOrderTask: {
                ChangeOrderUSID: changeOrderUSID, Instruction: text}}},
            headers: {"idoc-request": IDOC, "idoc-response": IDOC},
        })
            .then(() => { setInstruction(""); after(); })
            .catch(fail)
            .finally(() => setBusy(false));
    }, [instruction, changeOrderUSID, after]);

    const complete = useCallback((usid: string, status: string) => {
        setBusy(true);
        setError(null);
        AxiosFoundationAPI({
            method: "PUT", url: URL,
            data: {PlatformMessage: {"-idoc": IDOC, ChangeOrderTask: {USID: usid, Status: status}}},
            headers: {"idoc-request": IDOC, "idoc-response": IDOC},
        })
            .then(after).catch(fail).finally(() => setBusy(false));
    }, [after]);

    const remove = useCallback((usid: string) => {
        setBusy(true);
        setError(null);
        AxiosFoundationAPI({
            method: "DELETE", url: URL,
            data: {PlatformMessage: {"-idoc": IDOC, ChangeOrderTask: {USID: usid}}},
            headers: {"idoc-request": IDOC, "idoc-response": IDOC},
        })
            .then(after).catch(fail).finally(() => setBusy(false));
    }, [after]);

    const isDraft = state === "DRAFT";
    const mayEdit = isDraft && hasPrivilege(CHANGE_ORDER.EDIT);
    const outstanding = tasks.filter(task => task.Status !== "DONE" && task.Status !== "ABANDONED");

    // A change order with no tasks and no way to add one has nothing to say. Hiding the panel beats
    // an empty box captioned "Tasks (0)" on every change order anybody ever closes.
    if (!tasks.length && !mayEdit) return null;

    return (
        <Paper elevation={0}
               sx={{border: "1px solid #E4E7EC", borderRadius: "4px", p: 2, boxSizing: "border-box"}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1.5}}>
                <Typography sx={{fontSize: 16, fontWeight: 600, color: Blue3}}>
                    Implementation tasks
                </Typography>
                <Typography sx={{fontSize: 14, color: Grey5}}>({tasks.length})</Typography>
                {/* THE COUNT THAT DECIDES WHETHER IT CAN CLOSE, said plainly. "3 tasks" tells a
                    reader nothing about whether the package is waiting on them. */}
                {outstanding.length > 0 && (
                    <Typography sx={{fontSize: 13, color: "#8A5B00"}}>
                        {outstanding.length} outstanding &mdash; this change order cannot close until they are finished
                    </Typography>
                )}
            </Box>

            {error && <Typography sx={{fontSize: 14, color: "#D64535", mb: 1}}>{error}</Typography>}

            {tasks.map(task => {
                const settled = task.Status === "DONE" || task.Status === "ABANDONED";
                return (
                    <Box key={task.USID}
                         sx={{display: "flex", alignItems: "center", gap: 1, py: 0.75,
                              borderBottom: "1px solid #F2F4F7"}}>
                        <Typography sx={{fontSize: 14, flex: 1, minWidth: 0, color: Grey17,
                                         textDecoration: task.Status === "ABANDONED" ? "line-through" : "none"}}>
                            {task.Instruction}
                        </Typography>
                        {task.AssigneeName && (
                            <Typography sx={{fontSize: 13, color: Grey5}}>{task.AssigneeName}</Typography>
                        )}
                        {settled ? (
                            <Typography sx={{fontSize: 13, color: Grey5}}>
                                {task.Status === "DONE" ? "Done" : "Abandoned"}
                                {task.CompletedDatetime ? ` ${formatLocal(task.CompletedDatetime, undefined)}` : ""}
                            </Typography>
                        ) : (
                            <>
                                <Tooltip title="Mark done" placement="top">
                                    <span>
                                        <IconButton size="small" disabled={busy}
                                                    onClick={() => complete(task.USID, "DONE")}>
                                            <CheckCircleOutlined sx={{fontSize: 20, color: "#1E9E5A"}}/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                {/* ABANDONED IS NOT DELETED. Something decided unnecessary is part of
                                    the record of what was considered, and it settles the close
                                    without leaving the trail. */}
                                <Tooltip title="Not needed after all" placement="top">
                                    <span>
                                        <IconButton size="small" disabled={busy}
                                                    onClick={() => complete(task.USID, "ABANDONED")}>
                                            <DoNotDisturbAltOutlined sx={{fontSize: 20, color: Grey6}}/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </>
                        )}
                        {mayEdit && (
                            <Tooltip title="Remove this task" placement="top">
                                <span>
                                    <IconButton size="small" disabled={busy}
                                                onClick={() => remove(task.USID)}>
                                        <DeleteForever sx={{fontSize: 20, color: Grey6}}/>
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                );
            })}

            {mayEdit && (
                <Box sx={{display: "flex", alignItems: "center", gap: 1, mt: 1.5}}>
                    <TextField size="small" fullWidth value={instruction} disabled={busy}
                               placeholder="What has to happen before this change goes live?"
                               onChange={(event) => setInstruction(event.target.value)}
                               onKeyDown={(event) => { if (event.key === "Enter") add(); }}/>
                    <CCButton className={css_buttons.secondary} disabled={busy || !instruction.trim()}
                              onClick={add}>Add task</CCButton>
                </Box>
            )}

            {!mayEdit && isDraft === false && tasks.length > 0 && (
                <Typography sx={{fontSize: 13, color: Grey5, mt: 1.5}}>
                    Tasks can only be added or removed while the change order is a draft.
                </Typography>
            )}
        </Paper>
    );
}

export default ChangeOrderTasks;
